/**
 * HPN NAD3 Quiz Funnel v4 — Content Data Module
 * Source of truth: "HPN NAD3 QUIZ FUNNEL v4" spec doc.
 * All pair-adaptive copy lives here (not in section schema) because it is
 * legally reviewed, deterministic per goal-pair, and too large for Shopify's
 * per-section setting limits. See COMPLIANCE notes inline for edit rules.
 */
(function (root) {
  'use strict';

  // Canonical order used only to build a deterministic lookup key from an
  // unordered pair of goal picks (Energy+Sleep and Sleep+Energy => same key).
  var PAIR_ORDER = ['energy', 'focus', 'recovery', 'sleep', 'skin'];

  function pairKey(a, b) {
    return PAIR_ORDER.filter(function (k) { return k === a || k === b; }).join('_');
  }

  function scoreSku(goalKeys, stimulantKey) {
    var totals = { nad3: 0, nad3x: 0 };
    (goalKeys || []).forEach(function (key) {
      var goal = GOAL_OPTIONS.find(function (option) { return option.key === key; });
      if (goal) {
        totals.nad3 += goal.nad3;
        totals.nad3x += goal.nad3x;
      }
    });
    var stimulant = STIMULANT_OPTIONS.find(function (option) { return option.key === stimulantKey; });
    if (stimulant) {
      totals.nad3 += stimulant.nad3;
      totals.nad3x += stimulant.nad3x;
    }
    return totals.nad3x > totals.nad3 ? 'nad3x' : 'nad3';
  }

  var GOAL_SHORT_LABEL = {
    energy: 'Energy',
    focus: 'Focus',
    recovery: 'Recovery',
    sleep: 'Sleep',
    skin: 'Skin'
  };

  // Q2 — scored. Exactly 2 selections required.
  var GOAL_OPTIONS = [
    { key: 'energy', label: "I need more energy and motivation to get through my day", nad3x: 1, nad3: 0 },
    { key: 'focus', label: "Focus. I can't concentrate on my work.", nad3x: 1, nad3: 0 },
    { key: 'recovery', label: "I can't recover. Every day gets harder.", nad3x: 1, nad3: 1 },
    { key: 'sleep', label: "My sleep is terrible. I can't get a good night's rest.", nad3x: 0, nad3: 1 },
    { key: 'skin', label: "I'm starting to look older than I am. My skin needs help.", nad3x: 0, nad3: 1 }
  ];

  // Q3 — scored, highest routing weight. Never shown back to the user anywhere.
  var STIMULANT_OPTIONS = [
    { key: 'yes', label: 'Yes', nad3x: 3, nad3: 0 },
    { key: 'no', label: 'No', nad3x: 0, nad3: 3 },
    { key: 'sometimes', label: 'Sometimes', nad3x: 1.5, nad3: 0 }
  ];

  // Q1 — unscored segmentation only. Never referenced back in copy.
  var GENDER_OPTIONS = [
    { key: 'man', label: 'Man' },
    { key: 'woman', label: 'Woman' }
  ];

  // Q6 — universal, unscored (routing-wise) but decides the Break 2 variant.
  var PRIOR_OPTIONS = [
    { key: 'none', label: 'No, this would be my first' },
    { key: 'nmn', label: 'NMN' },
    { key: 'nr', label: 'NR or Niagen' },
    { key: 'iv', label: 'NAD+ IV drips or injections' },
    { key: 'none_worked', label: 'A few things. None of them did anything.' }
  ];

  // Q8 — universal, unscored identity close.
  var IDENTITY_OPTIONS = [
    { key: 'wake_ready', label: "I wake up before the alarm and I'm actually ready" },
    { key: 'day_left', label: 'I get through the whole day and still have something left at night' },
    { key: 'mirror', label: "I look in the mirror and see someone who's taking care of themselves" },
    { key: 'bounce_back', label: "A hard week doesn't take me a week to come back from" },
    { key: 'noticed', label: "People start asking what I'm doing differently" },
    { key: 'myself_again', label: 'I feel like myself again' }
  ];

  // Break 1 — three age bands. [AGE] is replaced at render time with Q4's answer.
  // COMPLIANCE (#5): the unsupported numeric placeholders from the brief are
  // deliberately omitted. Add a quantitative decline figure only after the
  // source and age-band calculation receive legal approval.
  var AGE_BANDS = {
    under35: {
      headline: 'At [AGE], you shouldn’t feel this way.',
      body: [
        'Most people your age don’t.',
        'You’ll get told you’re stressed. That you’re busy. That you need more sleep. Maybe some of that is true.',
        'But there’s a simpler answer, and almost nobody checks it.',
        'Your body runs on NAD+. It’s literally your spark plug. You can’t live without it. And right now you have more of it than you ever will again.',
        'So you’re not running out. You’re using more than you’re making.',
        'That’s the good news. You caught this early. Fix the supply now and you never have to find out what this feels like at 45.'
      ]
    },
    mid: {
      headline: 'At [AGE], you probably think this is just what [AGE] feels like.',
      body: [
        'Most people do. So they stop asking why.',
        'Here’s what’s actually going on.',
        'Your body runs on NAD+. It’s literally your spark plug. You can’t live without it. And you’re slowly losing more of it every single day.',
        'You made a lot of it at 20. By [AGE] you make much less.',
        'The two things you picked are what that feels like.'
      ]
    },
    over50: {
      headline: 'At [AGE], you’ve been told this is normal for your age.',
      body: [
        'But you know people your age who feel fine. Same age, different energy.',
        'So it isn’t just age.',
        'Your body runs on NAD+. It’s literally your spark plug. You can’t live without it. And you’ve been losing more of it every single day for years now.',
        'At [AGE] you make a lot less of it than you did at 20.',
        'Here’s the part nobody mentions. The further you’ve dropped, the more there is to get back.'
      ]
    }
  };

  function ageBandKey(age) {
    if (age < 35) return 'under35';
    if (age <= 49) return 'mid';
    return 'over50';
  }

  var UNIVERSAL_CLOSE = [
    'Nothing is wrong with you.',
    'Your body needs more than it’s getting right now. That’s it. And that part can be fixed.',
    'Four more questions and we’ll show you how.'
  ];

  // Break 1 bridges — one per goal pair.
  var BRIDGES = {
    energy_focus: ["Tired is one thing. Foggy is worse.", "Your brain uses more energy than any other part of you. So when energy runs low, your thinking slips first. That’s why the fog usually shows up before you feel tired.", "It isn’t two problems. It’s one."],
    energy_recovery: ["You use energy all day. Your body puts it back while you sleep.", "Right now you’re using more than you’re putting back.", "So you feel low during the day, and you don’t feel better in the morning."],
    energy_sleep: ["These two feed each other.", "When you don’t sleep well, your body has less energy the next day. When you’re low on energy, it gets harder to wind down at night.", "It’s a circle, and it gets tighter every year."],
    energy_skin: ["Your skin replaces itself faster than almost any other part of you. That takes a lot of energy.", "When your body runs low, it takes care of the critical things first. Your skin waits.", "That’s why it shows up on your face."],
    focus_recovery: ["You wake up and you don’t feel reset.", "Your body repairs itself while you sleep, and your brain clears itself out at the same time. Both need energy to do it.", "When there isn’t enough, neither one finishes. So you start the day still tired from yesterday."],
    focus_sleep: ["Your brain does its cleanup while you sleep.", "When you don’t sleep well, that cleanup doesn’t finish. You wake up foggy before the day has even started.", "The bad sleep and the fog are the same problem."],
    focus_skin: ["Other people notice both of these before you do.", "Clear thinking and healthy skin both take energy to maintain. When your body runs low, it sends that energy somewhere more urgent.", "These are the first two things it lets slide."],
    recovery_sleep: ["Your body does its repair work while you sleep. Not during the day. At night.", "When you don’t sleep well, that repair doesn’t happen.", "So you wake up still carrying yesterday."],
    recovery_skin: ["Repairing muscle and renewing skin are the same kind of work. Both take energy.", "When your body doesn’t have enough, both fall behind.", "You feel it first. Then you start to see it."],
    sleep_skin: ["Beauty sleep is a real thing.", "Your body does most of its repair work at night, and that includes your skin. Bad sleep means less repair.", "You feel it in the morning. You see it a few weeks later."]
  };

  // Q5 — "what it looks like", multi-select, not scored. One variant per pair.
  var Q5 = {
    energy_focus: { question: 'When you’re running low, what does that look like?', options: ["I get through the day but I’m not really thinking", "I can focus for an hour, then I’m done", "I’m awake but I’m on autopilot", "I push through and make mistakes I don’t normally make", "I do the easy work and put off anything that takes real thought"] },
    energy_recovery: { question: 'How long does it take you to bounce back?', options: ["One hard day costs me the next two", "I’m running low by Wednesday and empty by Friday", "I need the whole weekend just to feel normal", "I don’t bounce back anymore. I just keep going.", "It changes week to week and I can’t predict it"] },
    energy_sleep: { question: 'Walk us through a bad day.', options: ["I wake up tired and never catch up", "I’m fine in the morning, then I crash and can’t come back", "I get a second wind at night and then can’t fall asleep", "I’m tired all day and wide awake at bedtime", "I nap when I can and it doesn’t help"] },
    energy_skin: { question: 'When you’re running low, what shows first?', options: ["My face. People ask if I’m okay.", "My eyes. Dark circles, puffy, tired.", "My skin goes dull and nothing I put on it helps", "I look tired in every photo now", "I feel it before anyone sees it, but they see it eventually"] },
    focus_recovery: { question: 'What’s the hardest part of your week?', options: ["Getting started again after a hard workout", "Thinking clearly on days I’m sore or beat up", "Mondays. The weekend never fully resets me.", "Anything that takes concentration after a long day", "I can do one or the other. Not both."] },
    focus_sleep: { question: 'How does a bad night show up the next day?', options: ["I can’t hold a thought for more than a minute", "I’m slow. Everything takes longer than it should.", "I make careless mistakes", "I’m there but I’m not really listening", "I need caffeine just to think straight"] },
    focus_skin: { question: 'What made you start paying attention to this?', options: ["A photo of myself", "Somebody said something", "I couldn’t get through a normal workday", "I looked in the mirror and it caught me off guard", "It’s been building a while and I finally stopped ignoring it"] },
    recovery_sleep: { question: 'What does a bad night cost you?', options: ["I skip the workout", "I train anyway and feel it for days", "I stay sore longer than I should", "Everything aches a little more than it used to", "I’ve stopped planning hard days because I can’t predict the recovery"] },
    recovery_skin: { question: 'Where do you notice it most?', options: ["My face looks as tired as my body feels", "I’m sore and I look worn out at the same time", "After a hard week, both get worse together", "My skin changed around the same time my recovery did", "I noticed the recovery first. The mirror caught up later."] },
    sleep_skin: { question: 'After a bad stretch of sleep, what do you see?', options: ["Dark circles that don’t go away", "My skin looks dull and tired", "Puffiness, especially in the morning", "I just look older than I did a year ago", "It takes days before I look like myself again"] }
  };

  var BREAK2 = {
    variantA: {
      headline: 'If you’ve tried NMN, NR, or IV drips and felt nothing, here’s what actually happened.',
      body: [
        'None of them are fake. They all raise NAD+.',
        'They all do the same one thing, though. Add more.',
        'Here’s the part nobody explains.',
        'Picture a bathtub. NAD+ is the water. Early on it stays full and you never think about it. Then two things happen at once. The tap slows down. And the drain opens up.',
        'Almost every NAD+ product on the market pours more water in. That’s the entire strategy.',
        'The IV drip is that idea at its most expensive. Thousands of dollars to fill the tub in an afternoon, and within days you’re back where you started. Nothing changed about the drain.',
        'NAD3® was built for the drain.',
        'It supports your body’s own production, and it helps protect what you already have from burning off faster than you can replace it.',
        'The tap and the drain. Not just the tap.'
      ]
    },
    variantB: {
      headline: 'Before you pick one, here’s what almost all of them get wrong.',
      body: [
        'You’re about to walk into a category where nearly every product does the same one thing. Adds more NAD+.',
        'Here’s why that isn’t enough.',
        'Picture a bathtub. NAD+ is the water. Early on it stays full and you never think about it. Then two things happen at once. The tap slows down. And the drain opens up.',
        'Pouring more water into a draining tub is the entire strategy behind most of this category. It’s why people spend a few hundred dollars over four months and feel nothing.',
        'NAD3® was built for the drain.',
        'It supports your body’s own production, and it helps protect what you already have from burning off faster than you can replace it.',
        'The tap and the drain. Not just the tap.'
      ]
    }
  };

  // Q7 — "what it's costing them", multi-select, not scored.
  // options with exclusive:true clear every other selection in that step
  // (and are themselves cleared the moment a non-exclusive option is picked).
  var Q7 = {
    energy_focus: { question: 'What is this costing you?', options: [
      { label: "Work. I’m slower and I know it." },
      { label: "My best work. I’m not doing it anymore." },
      { label: "My patience with people I care about" },
      { label: "Everything after work. There’s nothing left for it." },
      { label: "Confidence. I don’t trust myself the way I used to." }
    ] },
    energy_recovery: { question: 'What have you cut back on?', options: [
      { label: "Training as hard as I want to" },
      { label: "Weekends. I need them to recover now." },
      { label: "Plans during the week" },
      { label: "Nothing yet, but I can feel it coming", exclusive: true },
      { label: "Nothing. I push through and pay for it later.", exclusive: true }
    ] },
    energy_sleep: { question: 'What would change if you slept well and woke up rested?', options: [
      { label: "I’d actually want to get up" },
      { label: "I’d stop needing caffeine to function" },
      { label: "I’d have something left at the end of the day" },
      { label: "I’d be better to the people around me" },
      { label: "I’d stop dreading the mornings" }
    ] },
    energy_skin: { question: 'What has this cost you?', options: [
      { label: "How I feel getting through a normal day" },
      { label: "How I look in photos" },
      { label: "Confidence in front of other people" },
      { label: "Energy for the things I used to enjoy" },
      { label: "People have started noticing, and that’s the worst part" }
    ] },
    focus_recovery: { question: 'Where does this hurt most?', options: [
      { label: "At work" },
      { label: "In the gym" },
      { label: "At home, with my family" },
      { label: "My own goals. They keep getting pushed." },
      { label: "My mood. I’m shorter with people than I want to be." }
    ] },
    focus_sleep: { question: 'What are you doing to get through it?', options: [
      { label: "Caffeine, more than I should" },
      { label: "Working longer to make up for being slower" },
      { label: "Going to bed earlier, and it isn’t helping" },
      { label: "Sleep aids or supplements" },
      { label: "Nothing. I’m just tired.", exclusive: true }
    ] },
    focus_skin: { question: 'What have you already tried?', options: [
      { label: "Better skincare" },
      { label: "Better sleep habits" },
      { label: "Vitamins or supplements" },
      { label: "A doctor visit" },
      { label: "I’ve tried things and none of it stuck" }
    ] },
    recovery_sleep: { question: 'What’s the first thing you’d want back?', options: [
      { label: "Waking up without an alarm and feeling ready" },
      { label: "Training hard without paying for it all week" },
      { label: "Sleeping through the night" },
      { label: "Not being sore all the time" },
      { label: "Feeling like my body is on my side again" }
    ] },
    recovery_skin: { question: 'What made this feel urgent?', options: [
      { label: "I look and feel older than I am" },
      { label: "Somebody said something" },
      { label: "A photo" },
      { label: "I couldn’t do something I used to do easily" },
      { label: "It’s been building and I’m done waiting it out" }
    ] },
    sleep_skin: { question: 'What have you already tried for it?', options: [
      { label: "Skincare products and routines" },
      { label: "Sleep habits. Earlier bedtime, no screens." },
      { label: "Supplements" },
      { label: "Facials or treatments" },
      { label: "I’ve tried things and none of it stuck" }
    ] }
  };

  // Result body copy — one block per (sku, pair). sku is 'nad3' or 'nad3x'.
  var RESULTS = {
    nad3: {
      energy_focus: ["You’re tired and focusing is only getting harder to do. You need your energy back at the source.", "NAD3® is that. NAD3® gives you more energy, sharper focus, and improved memory.", "Real support for your own NAD+ production, building week over week. Most people notice by week two or three. More of you, every day."],
      energy_recovery: ["You’re running on empty and your body isn’t bouncing back the way it used to.", "NAD3® goes after why. It gives you more energy, faster recovery, and better cellular repair.", "Real support for your own NAD+ production, which is what your body rebuilds with. It builds week over week. Train hard. Come back faster."],
      energy_sleep: ["Exhausted all day, and then you can’t shut off at night. Those are the same problem.", "NAD3® goes after it. It gives you more energy during the day, better sleep quality at night, and real support for managing stress and cortisol.", "Real support for your own NAD+ production, working while you sleep. Better days. Better nights. And it holds."],
      energy_skin: ["You’re running low, and it’s showing on your face.", "NAD3® works on both. It gives you more energy, healthier looking skin, and support for your own collagen production.", "Real support for your own NAD+ production, working where your skin renews itself. Feel it first. Then watch it show."],
      focus_recovery: ["You can’t think straight and your body won’t recover. Both run on the same thing.", "NAD3® rebuilds it. It gives you sharper focus, improved memory, and faster recovery.", "Real support for your own NAD+ production, which is what your body repairs with. Clearer thinking. Faster recovery. Built, not borrowed."],
      focus_sleep: ["Bad nights, foggy days, and it keeps getting worse.", "NAD3® goes after why. It gives you sharper focus, improved memory, better sleep quality, and real support for managing stress and cortisol.", "Real support for your own NAD+ production, working overnight when your brain does its cleanup. Sleep better. Wake up sharp."],
      focus_skin: ["Your head is foggy and your face looks tired. Same shortage, two places it shows.", "NAD3® goes after it. It gives you sharper focus, improved memory, healthier looking skin, and support for your own collagen production.", "Real support for your own NAD+ production, working under the surface. Sharper week by week. And it shows."],
      recovery_sleep: ["You’re not recovering, because repair happens at night and your nights aren’t working.", "NAD3® rebuilds what that repair runs on. It gives you better sleep quality, faster recovery, and real support for managing stress and cortisol.", "Real support for your own NAD+ production, working overnight when your body does its repair. Sleep deeper. Recover faster."],
      recovery_skin: ["Your body isn’t rebuilding and neither is your skin. Same job, same shortage.", "NAD3® rebuilds it. It gives you faster recovery, healthier looking skin, and support for your own collagen production.", "Real support for your own NAD+ production, which is what all repair runs on. Recover the way you used to. Look like it too."],
      sleep_skin: ["You’re not sleeping well, and it’s showing on your face. Night is when your skin does its repair.", "NAD3® goes after the reason. It gives you better sleep quality, healthier looking skin, support for your own collagen production, and real help managing stress and cortisol.", "Real support for your own NAD+ production, working overnight. Sleep deeper. Wake up looking like it."]
    },
    nad3x: {
      energy_focus: ["You’re tired and focusing is only getting harder to do. You need a reset.", "NAD3® Extra Strength is that. NAD3® Extra Strength gives you more energy, sharper focus, and improved memory.", "Instant Energy and Focus you’ll feel within 30 minutes. Underneath it, real support for your own NAD+ production. Hit the reset and feel it every day."],
      energy_recovery: ["You’re running on empty and your body isn’t bouncing back the way it used to. You need a reset.", "NAD3® Extra Strength is that. It gives you more energy, faster recovery, and better cellular repair.", "Instant Energy you’ll feel within 30 minutes. Underneath it, real support for your own NAD+ production, which is what your body rebuilds with. Train hard. Come back faster."],
      energy_sleep: ["You’re exhausted all day and then you can’t shut off at night. You need a reset.", "NAD3® Extra Strength is that. It gives you more energy during the day, better sleep quality at night, and real support for managing stress and cortisol.", "Instant Energy you’ll feel within 30 minutes. Underneath it, real support for your own NAD+ production, working while you sleep. Get your days back. Then get your nights back."],
      energy_skin: ["You’re running low and it’s showing on your face. You need a reset.", "NAD3® Extra Strength is that. It gives you more energy, healthier looking skin, and support for your own collagen production.", "Instant Energy you’ll feel within 30 minutes. Underneath it, real support for your own NAD+ production, working where your skin renews itself. Feel better. Then look it."],
      focus_recovery: ["You can’t think straight and your body won’t recover. You need a reset.", "NAD3® Extra Strength is that. It gives you sharper focus, improved memory, and faster recovery.", "Instant Focus you’ll feel within 30 minutes. Underneath it, real support for your own NAD+ production, which is what your body repairs with. Show up sharp. Come back faster."],
      focus_sleep: ["Bad nights, foggy days, and it keeps getting worse. You need a reset.", "NAD3® Extra Strength is that. It gives you sharper focus, improved memory, better sleep quality, and real support for managing stress and cortisol.", "Instant Focus you’ll feel within 30 minutes. Underneath it, real support for your own NAD+ production, working overnight when your brain does its cleanup. Sleep better. Wake up sharp."],
      focus_skin: ["Your head is foggy and your face looks tired. You need a reset.", "NAD3® Extra Strength is that. It gives you sharper focus, improved memory, healthier looking skin, and support for your own collagen production.", "Instant Focus you’ll feel within 30 minutes. Underneath it, real support for your own NAD+ production, working under the surface. Sharper. And it shows."],
      recovery_sleep: ["You’re not recovering, and your nights aren’t helping. You need a reset.", "NAD3® Extra Strength is that. It gives you better sleep quality, faster recovery, and real support for managing stress and cortisol.", "Energy to get through the day, within 30 minutes. Underneath it, real support for your own NAD+ production, working overnight when your body does its repair. Sleep deeper. Recover faster."],
      recovery_skin: ["Your body isn’t rebuilding and neither is your skin. You need a reset.", "NAD3® Extra Strength is that. It gives you faster recovery, healthier looking skin, and support for your own collagen production.", "Energy to get through the day, within 30 minutes. Underneath it, real support for your own NAD+ production, which is what all repair runs on. Recover faster. Look like it too."],
      sleep_skin: ["You’re not sleeping well and it’s showing on your face. You need a reset.", "NAD3® Extra Strength is that. It gives you better sleep quality, healthier looking skin, support for your own collagen production, and real help managing stress and cortisol.", "Energy to get through the day, within 30 minutes. Underneath it, real support for your own NAD+ production, working overnight when your skin repairs itself. Sleep deeper. Wake up looking like it."]
    }
  };

  var RESULT_CLINICAL_CLAIM = "And it's the only all-natural, clinically studied formula on the market that can back that up.";
  Object.keys(RESULTS).forEach(function (sku) {
    Object.keys(RESULTS[sku]).forEach(function (pair) {
      RESULTS[sku][pair][1] += ' ' + RESULT_CLINICAL_CLAIM;
    });
  });

  root.HPNQuizContent = {
    pairKey: pairKey,
    scoreSku: scoreSku,
    GOAL_SHORT_LABEL: GOAL_SHORT_LABEL,
    GOAL_OPTIONS: GOAL_OPTIONS,
    STIMULANT_OPTIONS: STIMULANT_OPTIONS,
    GENDER_OPTIONS: GENDER_OPTIONS,
    PRIOR_OPTIONS: PRIOR_OPTIONS,
    IDENTITY_OPTIONS: IDENTITY_OPTIONS,
    AGE_BANDS: AGE_BANDS,
    ageBandKey: ageBandKey,
    UNIVERSAL_CLOSE: UNIVERSAL_CLOSE,
    BRIDGES: BRIDGES,
    Q5: Q5,
    BREAK2: BREAK2,
    Q7: Q7,
    RESULTS: RESULTS
  };

})(window);
